import { MODULE_LABELS } from "@dentacore/shared-types";
import type { ModuleName } from "@dentacore/shared-types";

import { MODULE_BLURB } from "../shell/locked-module";
import { useModules } from "../shell/modules-context";
import { PaneHead } from "./pane";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { useWriter } from "./use-writer";
import { PlanCard } from "./plan-card";

const ORDER: readonly ModuleName[] = ["SUPPLY", "ACCOUNTING", "CHATBOT"];

/**
 * **What the clinic has, and asking for the rest** (STORY-188b, MOD-D11).
 *
 * The base plan is always there; these are the three add-ons. A module the
 * clinic does not have shows what it does and an *Add* button, which sends
 * nothing outside the product — it leaves a note an operator sees. Switching a
 * module on or off is the operator's alone.
 */
export function ModulesPane(): React.JSX.Element {
  const { states, request, requested } = useModules();
  const { busy, toast, dismiss, run } = useWriter();

  return (
    <>
      <PaneHead title="Modules" />
      <PlanCard />
      <section className={styles.card} data-testid="modules-pane">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Add-ons</h2>
        </div>
        <div className={styles.cardBody}>
          <p className={styles.lead}>
            Everything else in DentaCore is part of your plan. These three are added separately.
          </p>
          {states === null ? null : (
            <ul>
              {ORDER.map((module) => {
                const state = states.find((s) => s.module === module);
                const on = state?.enabled === true;
                return (
                  <li key={module} data-testid={`module-row-${module}`}>
                    <strong>{MODULE_LABELS[module]}</strong> · {on ? "on" : "not added"}
                    <div>{MODULE_BLURB[module]}</div>
                    {!on &&
                      (requested(module) ? (
                        <span data-testid={`module-asked-${module}`}>
                          You have asked for it. We will switch it on for you.
                        </span>
                      ) : (
                        <button
                          type="button"
                          className={styles.ghost}
                          disabled={busy}
                          data-testid={`module-add-${module}`}
                          onClick={() => {
                            void run(
                              () => request(module),
                              () => Promise.resolve(),
                              `Asked for ${MODULE_LABELS[module]}.`,
                            );
                          }}
                        >
                          Add {MODULE_LABELS[module]}
                        </button>
                      ))}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}
